import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { onecEnvelopeDecoder, type OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import type { OneCStructure } from '../model/types'
import { decodeStructure } from './contracts'
import { ONEC_STRUCTURE_URLS } from './urls'

/**
 * Структура базы 1С в ОБЩЕМ `baseApi` (R04): ОДИН запрос на базу для экрана
 * «Структура 1С» и для всех выпадающих списков складов пакета (поставки,
 * инвентаризация, ценники). rid — аргумент (часть ключа кэша) и часть тега.
 * Служба читает структуру из 1С на лету (RP-LSB1, память — до минуты); в
 * браузер она не сохраняется — живёт только в кэше запроса.
 */
export const ONEC_STRUCTURE_TAG = 'OneCStructure' as const

export interface StructureArgs {
  resourceId: string
}

export const structureTag = (resourceId: string) => resourceTag(ONEC_STRUCTURE_TAG, normalizeRid(resourceId), 'STRUCTURE')

export const structureApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_STRUCTURE_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    onecStructure: build.query<OneCEnvelope<OneCStructure>, StructureArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_STRUCTURE_URLS.structure(resourceId),
        // Чтение идёт в 1С: отказ показывается человеку сразу, следующий запрос — его «Повторить», а не повтор transport.
        noRetry: true,
        decode: onecEnvelopeDecoder(resourceId, decodeStructure(resourceId)),
      }),
      providesTags: (_result, _error, { resourceId }) => [structureTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
    /**
     * «Обновить из 1С»: служба забывает память базы и читает структуру из 1С
     * заново (каждое нажатие, без паузы). Ответ становится ответом запроса
     * структуры — в том числе после его отказа: окно снова показывает дерево.
     * Записи в 1С нет; повтор безопасен, но делает его человек.
     */
    onecStructureRefresh: build.mutation<OneCEnvelope<OneCStructure>, StructureArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_STRUCTURE_URLS.refresh(resourceId),
        method: 'POST',
        decode: onecEnvelopeDecoder(resourceId, decodeStructure(resourceId)),
      }),
      async onQueryStarted({ resourceId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // upsert, а не patch: patch оставил бы прежний отказ чтения поверх свежей структуры.
          void dispatch(structureApi.util.upsertQueryData('onecStructure', { resourceId }, data))
        } catch {
          // Отказ показывает сам результат мутации; кэш не трогаем.
        }
      },
    }),
  }),
})

export const { onecStructure, onecStructureRefresh } = structureApi.endpoints

/** Перечитать структуру базы («Повторить» после отказа 1С): тег — внутреннее дело пакета. */
export const reloadStructure = (resourceId: string) => structureApi.util.invalidateTags([structureTag(resourceId)])
